import React, { useState, useContext, useRef, useEffect } from 'react';
import { AsNumber, AsText, AsDatePicker, AsButton, AsSelect, ScrollableArea } from '@adserve/adserve-react-components';
import {
    invoicingManagerHeadCellStyle,
    invoicingManagerSummaryCellStyle,
    invoicingManagerTableStyle,
    labelStyle,
    datePickerInputStyle,
    datePickerButtonStyle
} from './BespokeBillingStyles';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import BespokeInvoiceManagerRow from './BespokeInvoiceManagerRow';
import { v4 } from 'uuid';
import moment from 'moment';
import { GetOpenBillingPeriod } from '../../../services/CampaignsDataService';
import AsTextWithStatus from '../../generic/AsTextWithStatus';
import { DefaultInputStatus, ErrorInputStatus } from '../../../services/UtilsService';
import { GetRegistryValue } from '../../../services/DataService';

const inputStyle = { 
    width: '94%',
    marginLeft: 10,
    marginRight: 10
};

const BespokeInvoiceManager = ({
    invoice,
    invoices,
    campaign,
    offairTypes,
    vatRates,
    onInvoiceDateChange,
    onNarrativeChange,
    onPoNumberChange,
    onApplyClick,
    onAddClick,
    calcVatRate,
    defaultVatRate,
    defaultVatDate,
    onDeleteInvoiceLine,
    readOnly = false,
    vatDates
}) => {
    const { dateFormat, formatCurrency, formatNumber } = useContext(SystemSettingsContext);

    // const minDate = new Date();
    // minDate.setDate(minDate.getDate() - 1);

    const [editingInvoiceLine, setEditingInvoiceLine] = useState({});
    const [changesMade, setChangesMade] = useState(false);
    const scrollAreaRef = useRef(null);
    const [scrollPx, setScrollPx] = useState(0);
    const [billingPeriod, setBillingPeriod] = useState({});
    const [descriptionStatus, setDescriptionStatus] = useState(() => DefaultInputStatus());
    const [descriptionCharLength, setDescriptionCharLength] = useState(60);
    const lastDayOfCurrentMonth = moment().utc(true).endOf('month').startOf('day');

    useEffect(() => {
        setEditingInvoiceLine({
            ...editingInvoiceLine,
            commissionRate: !campaign.barterAgencyAccountId ? campaign.commissionRate : campaign.barterCommissionRate,
            vatRateId: campaign?.vatId,
        });

    }, [campaign]);

    useEffect(() => {
        getBillingPeriod();
        GetRegistryValue('SAPDESCMAXLENGTH')
            .then((result) => {
                if (result?.value)
                    setDescriptionCharLength(result?.value);
            })
            .catch((error) => {
                console.log(error)
            });
    }, []);

    useEffect(() => {
        invoice.invoiceDate = lastDayOfCurrentMonth;
    }, [editingInvoiceLine?.offAirTypeId]);

    let invoiceLines = invoice?.billingPlanInvoiceLines ?? [];

    const handleApplyClick = () => {
        onApplyClick(invoice);
        setChangesMade(false);
    };

    const handleAddClick = () => {
        // Generate a temp uuid, if we don't have one.
        editingInvoiceLine.vatDateId = vatDates?.find((vr) => vr.vatId === editingInvoiceLine?.vatRateId).id
        const vr = vatDates?.find((vr) => vr.id === editingInvoiceLine.vatDateId);
        onAddClick({
            id: v4(),
            ...editingInvoiceLine,
            vatRateId: vr.vatId,
            vatDateId: vr.id,
            vatRate: vr.rate,
        },
        invoice.invoiceDate ?? lastDayOfCurrentMonth);
        setChangesMade(true);
        clearAddInvoiceLine();
    };

    const onDescriptionChange = (e) => {
        setEditingInvoiceLine({ ...editingInvoiceLine, name: e.target.value });
    };

    useEffect(() => {
        if (editingInvoiceLine?.name?.length > descriptionCharLength) {
            setDescriptionStatus(ErrorInputStatus(window.translate('Description is over the maximum character limit')));
        } else {
            setDescriptionStatus(DefaultInputStatus());
        }
    }, [editingInvoiceLine.name]);

    const onAirtimeChange = (e) => {
        const val = e.target.value;
        if (val.length) {
            const lastChar = val.slice(-1);
            const last2Chars = val.slice(-2);
            if (lastChar === '.' || last2Chars === '.0') {
                setEditingInvoiceLine({ ...editingInvoiceLine, airtimeAmount: val, offAirAmount: 0 });
                return;
            }
        }
        setEditingInvoiceLine({ ...editingInvoiceLine, airtimeAmount: parseFloat(e.target.value || 0), offAirAmount: 0 });
    };

    const onCommissionChange = (e) => {
        const val = e.target.value;
        if (val.length) {
            const lastChar = val.slice(-1);
            const last2Chars = val.slice(-2);
            if (lastChar === '.' || last2Chars === '.0') {
                setEditingInvoiceLine({ ...editingInvoiceLine, commissionRate: val });
                return;
            }
        }
        setEditingInvoiceLine({
            ...editingInvoiceLine,
            commissionRate: e.target.value > 100 ? 100 : e.target.value
        });
    };

    const onVatRateChange = (e) => {
        const vr = vatRates.find((vr) => vr.id === e.target.value);
        setEditingInvoiceLine({
            ...editingInvoiceLine,
            vatRateId: e.target.value || defaultVatRate.id,
            vatDateId: vr.vatDates[0].id,
            vatRate: vr.vatDates[0].rate
        });
    };

    const onEditInvoiceLine = (il) => {
        if (il.vatDateId === window.emptyGuid) {
            let vatRateId = window.emptyGuid;
            if (!campaign?.vatId) {
                vatRateId = vatRates.find((vr) => vr.isDefault).id;
            } else {
                vatRateId = campaign?.vatId;
            }
            il.vatDateId = vatDates?.find((vr) => vr.vatId === vatRateId).id;
        }
        const vr = vatDates?.find((vr) => vr.id === il.vatDateId);
        setEditingInvoiceLine({
            vatRateId: vr.vatId,
            vatDateId: vr.id,
            vatRate: vr.rate,
            ...il
        });
    };

    const handleDeleteInvoiceLine = (il) => {
        onDeleteInvoiceLine(il);
        setChangesMade(true);
    };

    const clearAddInvoiceLine = () => {
        setEditingInvoiceLine({
            offAirTypeId: window.emptyGuid,
            offAirAmount: 0,
            airtimeAmount: 0,
            commissionRate: !campaign.barterAgencyAccountId ? campaign.commissionRate : campaign.barterCommissionRate,
            vatRateId: campaign?.vatId,
            vatDateId: defaultVatDate.id,
            vatRate: defaultVatDate.rate
        });
    };

    // Remove editing line from list, if it has been saved previously
    if (editingInvoiceLine.id) {
        invoiceLines = invoiceLines.filter((il) => il.id !== editingInvoiceLine.id);
    }

    const visibleRows = 6;
    const emptyRows = () => {
        const retVal = [];
        for (let i = invoiceLines.length; i < visibleRows; i++) {
            retVal.push(
                <BespokeInvoiceManagerRow
                    key={i}
                    index={i}
                    offairTypes={offairTypes}
                    onEditInvoiceLine={onEditInvoiceLine}
                />
            );
        }
        return retVal;
    };

    const calcTotal = () => {
        if (typeof calcVatRate !== 'function') {
            return {};
        }
        const amount = parseFloat(
            parseFloat(editingInvoiceLine.airtimeAmount || 0) + parseFloat(editingInvoiceLine.offAirAmount || 0)
        );
        const commission = parseFloat((amount * editingInvoiceLine.commissionRate) / 100);
        const amountLessCommission = amount - commission;
        const vat = (amountLessCommission * parseFloat(calcVatRate(editingInvoiceLine.vatRateId))) / 100;

        let airtimeTotal = 0;
        let offAirTotal = 0;
        let commissionTotal = 0;
        let vatTotal = 0;
        let overallTotal = 0;
        invoiceLines?.forEach((il) => {
            const airtimeAmount = parseFloat(il.airtimeAmount || 0);
            const offAirAmount = parseFloat(il.offAirAmount || 0);
            const amount = airtimeAmount + offAirAmount;
            const commission = (amount * il.commissionRate) / 100;
            const amountLessCommission = amount - commission;
            if (il.vatDateId === window.emptyGuid) {
                let vatRateId = window.emptyGuid;
                if (!campaign?.vatId) {
                    vatRateId = vatRates.find((vr) => vr.isDefault).id;
                } else {
                    vatRateId = campaign?.vatId;
                }
                il.vatDateId = vatDates?.find((vr) => vr.vatId === vatRateId).id;
            }
            const vatRateId = vatDates.find((vd) => vd.id === il.vatDateId)?.vatId;
            const vatRateAmount = (!isNaN(calcVatRate(il.vatRateId)) ? calcVatRate(vatRateId) : 0);
            const vat = (amountLessCommission * vatRateAmount) / 100;
            const overall = amount - commission + (!isNaN(vat) ? vat : 0);
            airtimeTotal += airtimeAmount;
            offAirTotal += offAirAmount;
            commissionTotal += commission;
            vatTotal += (!isNaN(vat) ? vat : 0);
            overallTotal += overall;
        });
        return {
            editingTotal: amount - commission + (!isNaN(vat) ? vat : 0),
            editingCommission: commission,
            editingVat: (!isNaN(vat) ? vat : 0),
            airtimeTotal,
            offAirTotal,
            commissionTotal,
            vatTotal,
            overallTotal
        };
    };

    const totals = calcTotal();

    const getInvoiceDate = () => {
        if (editingInvoiceLine.id) {
            return invoice.invoiceDate ?? lastDayOfCurrentMonth
        } else {
            let maxDate = invoice.invoiceDate ?? lastDayOfCurrentMonth;
            invoices?.forEach((i) => {
                i.billingPlanInvoiceLines?.forEach((il, i) => {
                    const ilDate = new Date(il.invoiceLineDate).setHours(1);
                    if (ilDate > maxDate) {
                        maxDate = ilDate;
                    }
                });
            });
            return maxDate;
        }
    };

    const getBillingPeriod = async () => {
        const data = await GetOpenBillingPeriod();
        data.startDate = moment(data?.startDate);
        data.endDate = moment(data?.endDate);
        setBillingPeriod(data);
    };

    // ASSP-6309
    // Add optional narrative as invoice name in place of first or concatenated item descriptions
    // Keep invoice meta data controls in place not the other side of the screen
    // Set even columnn and input widths
    //
    const narrativeLabel = 'Name or narrative (optional eg summary of multiple descriptions)';

    return (
        <div style={{ display: 'grid', width: 1944 }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end'
                }}
            >
                <div>
                    <label style={labelStyle}>{window.translate('Invoice Date *')}</label>
                    <AsDatePicker
                        id='invoice-date'
                        inputStyle={{ ...datePickerInputStyle }}
                        buttonStyle={datePickerButtonStyle}
                        currentDate={invoice.invoiceDate}
                        onChange={onInvoiceDateChange}
                        handleMultiple={true}
                        minDate={billingPeriod?.startDate}
                        dateFormat={dateFormat}
                        disabled={readOnly || invoiceLines.length === 0}
                    />
                </div>
                <div style={{ boxSizing: 'border-box', paddingTop: 4, marginRight: 20 }}>
                    <label style={labelStyle}>{window.translate('P.O. Number')}</label>
                    <AsText
                        id='invoice-po'
                        inputStyle={{ width: 160 }}
                        value={invoice?.poNumber}
                        onChange={onPoNumberChange}
                        disabled={readOnly || invoiceLines.length === 0}
                    />
                </div>
                <div style={{ boxSizing: 'border-box', paddingTop: 4, marginRight: 24 }}>
                    <label style={labelStyle}>{window.translate(narrativeLabel)}</label>
                    <AsText
                        id='narrative'
                        inputStyle={{ width: 784 }}
                        value={invoice?.name}
                        onChange={onNarrativeChange}
                        disabled={readOnly || invoiceLines.length === 0}
                        // disabled={readOnly || invoiceLines.length < 2} // Only if multiple lines
                    />
                </div>
                <div style={{ paddingRight: 87, paddingBottom: 3 }}>
                    <AsButton
                        style={{
                            height: 38,
                            width: 72,
                            fontSize: 14,
                            fontWeight: 'bold'
                        }}
                        handleClick={handleApplyClick}
                        disabled={readOnly || !changesMade || !invoice.invoiceDate}
                    >
                        Apply
                    </AsButton>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: 87 }}>
                <div style={{ width: 1688, marginTop: 17 }}>
                    {scrollPx > 0 && (
                        <>
                            <div
                                style={{
                                    position: 'absolute',
                                    zIndex: 9000,
                                    height: 1,
                                    width: 1718,
                                    backgroundColor: '#E3E3E3'
                                }}
                            />
                            <div
                                style={{
                                    position: 'absolute',
                                    marginTop: 79,
                                    zIndex: 9000,
                                    height: 1,
                                    width: 1718,
                                    backgroundColor: '#FFFFFF'
                                }}
                            />
                            <div
                                style={{
                                    position: 'absolute',
                                    marginTop: 80,
                                    zIndex: 9000,
                                    height: 1,
                                    width: 1718,
                                    backgroundColor: '#E3E3E3'
                                }}
                            />
                        </>
                    )}
                    <ScrollableArea
                        id={'pending-invoiceLines-container'}
                        ref={scrollAreaRef}
                        height={331}
                        scrollSpeed={8}
                        scrollPx={scrollPx}
                        onScroll={setScrollPx}
                        scrollableLength={scrollAreaRef?.current?.scrollHeight}
                        maxVisibleLength={scrollAreaRef?.current?.clientHeight}
                    >
                        <table style={invoicingManagerTableStyle}>
                            <thead>
                                <tr>
                                    <td align='center' style={invoicingManagerHeadCellStyle} colSpan={2}>
                                        <div
                                            style={{
                                                margin: '0 10px 48px 10px',
                                                width: 81,
                                                textAlign: 'center'
                                            }}
                                        >
                                            {window.translate('Actions')}
                                        </div>
                                    </td>
                                    <td align='center' style={invoicingManagerHeadCellStyle} width={847}>
                                        <div style={{ width: 827 }}>
                                            <span style={{ paddingLeft: 10 }}>{window.translate('Description')}</span>
                                            <AsTextWithStatus
                                                onChange={onDescriptionChange}
                                                value={editingInvoiceLine?.name}
                                                disabled={readOnly}
                                                labelStyle={labelStyle}
                                                inputStyle={{
                                                    margin: '6px 10px 0 10px',
                                                    width: '96%',
                                                    ...(!readOnly
                                                        ? {
                                                            borderColor: descriptionStatus.statusColor,
                                                            backgroundColor: descriptionStatus.statusBGColor
                                                                ? descriptionStatus.statusBGColor
                                                                : '#FFFDF4'
                                                        }
                                                        : {})
                                                }}
                                                statusMessage={
                                                    descriptionStatus.statusMessage
                                                }
                                                statusColor={descriptionStatus.statusColor}
                                                statusMessageStyle={{ paddingLeft: 10 }}
                                            />
                                        </div>
                                    </td>
                                    <td align='center' style={invoicingManagerHeadCellStyle} width={135}>
                                        <div style={{ width: 115 }}>
                                            <span style={{ paddingLeft: 10 }}>{window.translate('Amount')}</span>
                                            <AsNumber
                                                value={editingInvoiceLine?.airtimeAmount || editingInvoiceLine?.offAirAmount}
                                                allowFloat={true}
                                                inputStyle={{
                                                    margin: '6px 10px 0 10px',
                                                    width: '70%'
                                                }}
                                                onChange={onAirtimeChange}
                                                disabled={readOnly}
                                                formatCurrency={formatCurrency}
                                            />
                                        </div>
                                    </td>
                                    <td align='center' style={invoicingManagerHeadCellStyle} width={135}>
                                        <div style={{ width: 115 }}>
                                            <span style={{ paddingLeft: 10 }}>{window.translate('Commission')} %</span>
                                            <AsNumber
                                                value={editingInvoiceLine?.commissionRate}
                                                allowFloat={true}
                                                inputStyle={{
                                                    margin: '6px 10px 0 10px',
                                                    width: '70%'
                                                }}
                                                onChange={onCommissionChange}
                                                formatNumber={formatNumber}
                                                disabled={readOnly}
                                            />
                                        </div>
                                    </td>
                                    <td align='center' style={invoicingManagerHeadCellStyle} width={135}>
                                        <div style={{ width: 115 }}>
                                            <span style={{ paddingLeft: 10 }}>{window.translate('Commission')}</span>
                                            <AsNumber
                                                value={totals?.editingCommission}
                                                allowFloat={true}
                                                inputStyle={{
                                                    margin: '6px 10px 0 10px',
                                                    width: '70%'
                                                }}
                                                formatNumber={formatCurrency}
                                                disabled={true}
                                            />
                                        </div>
                                    </td>
                                    <td align='center' style={invoicingManagerHeadCellStyle} width={135}>
                                        <div style={{ width: 115 }}>
                                            <span style={{ paddingLeft: 10 }}>{window.translate('VAT Rate')}</span>
                                            <AsSelect
                                                id='vat-rate'
                                                name='vatRateId'
                                                values={vatRates}
                                                value={editingInvoiceLine?.vatRateId}
                                                inputStyle={{
                                                    margin: '6px 0px 0 10px',
                                                    width: 106, //'70%'
                                                }}
                                                onChange={onVatRateChange}
                                                disabled={readOnly}
                                            />
                                        </div>
                                    </td>
                                    <td align='center' style={invoicingManagerHeadCellStyle} width={135}>
                                        <div style={{ width: 115 }}>
                                            <span style={{ paddingLeft: 10 }}>{window.translate('VAT')}</span>
                                            <AsNumber
                                                value={totals?.editingVat}
                                                allowFloat={true}
                                                inputStyle={{
                                                    margin: '6px 10px 0 10px',
                                                    width: '70%'
                                                }}
                                                formatNumber={formatCurrency}
                                                disabled={true}
                                            />
                                        </div>
                                    </td>
                                    <td align='center' style={invoicingManagerHeadCellStyle} width={135}>
                                        <div style={{ width: 115 }}>
                                            <span style={{ paddingLeft: 10 }}>{window.translate('Total')}</span>
                                            <AsNumber
                                                value={parseFloat(totals.editingTotal || 0).toFixed(2)}
                                                disabled={true}
                                                allowFloat={true}
                                                inputStyle={{
                                                    margin: '6px 10px 0 10px',
                                                    width: '70%'
                                                }}
                                                formatCurrency={formatCurrency}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                {invoiceLines?.map((il, i) => (
                                    <BespokeInvoiceManagerRow
                                        key={i}
                                        onEditInvoiceLine={onEditInvoiceLine}
                                        onDeleteInvoiceLine={handleDeleteInvoiceLine}
                                        index={i}
                                        invoiceLine={il}
                                        offairTypes={offairTypes}
                                        calcVatRate={calcVatRate}
                                        disabled={readOnly}
                                    />
                                ))}
                                {emptyRows()}
                            </tbody>
                        </table>
                    </ScrollableArea>
                    <div style={{ display: 'flex', width: 1762, marginTop: -1 }}>
                        <div
                            style={{
                                ...invoicingManagerSummaryCellStyle,
                                padding: 10,
                                width: 53
                            }}
                        ></div>
                        <div
                            style={{
                                ...invoicingManagerSummaryCellStyle,
                                padding: 10,
                                width: 52,
                                borderLeftWidth: 0
                            }}
                        ></div>
                        <div
                            style={{
                                ...invoicingManagerSummaryCellStyle,
                                padding: 10,
                                width: 847,
                                borderLeftWidth: 0
                            }}
                        >
                            Total
                        </div>
                        <div
                            style={{
                                ...invoicingManagerSummaryCellStyle,
                                padding: 10,
                                width: 135,
                                borderLeftWidth: 0
                            }}
                        >
                            {formatCurrency(totals.airtimeTotal) || '£0.00'}
                        </div>
                        <div
                            style={{
                                ...invoicingManagerSummaryCellStyle,
                                padding: 10,
                                width: 135,
                                borderLeftWidth: 0
                            }}
                        ></div>
                        <div
                            style={{
                                ...invoicingManagerSummaryCellStyle,
                                padding: 10,
                                width: 135,
                                borderLeftWidth: 0
                            }}
                        >
                            {formatCurrency(totals.commissionTotal) || '£0.00'}
                        </div>
                        <div
                            style={{
                                ...invoicingManagerSummaryCellStyle,
                                paddingRight: 10,
                                width: 135,
                                borderLeftWidth: 0
                            }}
                        ></div>
                        <div
                            style={{
                                ...invoicingManagerSummaryCellStyle,
                                padding: 10,
                                width: 135,
                                borderLeftWidth: 0
                            }}
                        >
                            {formatCurrency(totals.vatTotal) || '£0.00'}
                        </div>
                        <div
                            style={{
                                ...invoicingManagerSummaryCellStyle,
                                padding: 10,
                                width: 135,
                                borderLeftWidth: 0
                            }}
                        >
                            {formatCurrency(totals.overallTotal) || '£0.00'}
                        </div>
                    </div>
                </div>
                <div style={{ marginTop: 52, marginLeft: 48 }}>
                    <AsButton
                        style={{
                            height: 38,
                            width: 72,
                            fontSize: 14,
                            fontWeight: 'bold'
                        }}
                        disabled={readOnly || !editingInvoiceLine.name || invoiceLines.length >= 7}
                        handleClick={handleAddClick}
                    >
                        {editingInvoiceLine.id ? 'Update' : 'Add'}
                    </AsButton>
                </div>
            </div>
        </div>
    );
};

export default BespokeInvoiceManager;
